import React, { useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Popup } from "react-leaflet";
import { Button, IconButton, Tooltip, Box, Typography, Divider, styled } from "@mui/material";
import { useMap } from 'react-leaflet';
import {
    getProblems,
    getAcknowldgedTriggerUrl,
    violationsFixations,
    getNote,
    getGuestNote,
    getBitrixNote
} from '../shared/helper';
// import { CurrentComplexContext } from '../../context';
import PopupLinksDropdown from '../shared/PopupLinksDropdown';
import PopupScriptsDropdown from '../shared/PopupScriptsDropdown';
import PopupBitrixDropdown from '../shared/PopupBitrixDropdown';
import PopupInfoButton from '../shared/PopupInfoButton';
import { useSelector, useDispatch } from 'react-redux';
import { addNotification } from '../../store/notificationSlice';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
// import { CopyToClipboard } from 'react-copy-to-clipboard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import InfoIcon from '@mui/icons-material/Info';
import BugReportIcon from '@mui/icons-material/BugReport';
import NotesIcon from '@mui/icons-material/Notes';
import WebIcon from '@mui/icons-material/Web';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EngineeringIcon from '@mui/icons-material/Engineering';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import HostsDataDialog from '../modals/HostsDataDialog';
// Функция для получения цвета SVG иконки в зависимости от темы
const getSvgColor = (type) => {
    const colors = {
        coordinates: 'var(--info-color)',
        speed: 'var(--success-color)',
        time: 'var(--warning-color)',
        course: 'var(--secondary-color)',
        warning: 'var(--warning-color)',
        comment: 'var(--secondary-color)',
        fixation: 'var(--success-color)',
        violation: 'var(--error-color)'
    };
    return colors[type] || 'var(--text-secondary)';
};


const TypographyStyled = styled(Typography)(() => ({
    fontWeight: 500,
    transform: 'scale(1, 1.03)',
    letterSpacing: 0.5,
    transition: 'all 200ms',
    margin: '0px !important',
    '&:hover': {
        transform: 'scale(1.02, 1.05)',
    },
}));
// Стили для компонента
const getStyles = () => ({
    // Стили для блоков
    blockContainer: {
        border: '1px solid var(--divider-color)',
        borderRadius: { xs: '6px', md: '8px' },
        padding: { xs: '4px 8px', md: '6px 10px' },
        marginBottom: '2px',
        backgroundColor: 'var(--background-default)',
        transition: 'all 200ms ease-in-out',
        position: 'relative',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        '&:hover': {
            borderColor: 'var(--primary-color)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }
    },
    blockHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: { xs: '4px', md: '6px' },
        marginBottom: { xs: '4px', md: '6px' },
        paddingBottom: { xs: '3px', md: '4px' },
        borderBottom: '1px solid var(--divider-color)',
    },
    blockHeaderIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    blockHeaderTitle: {
        fontSize: { xs: '9px', md: '10px' },
        fontWeight: 600,
        color: 'var(--text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    blockContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '3px', md: '4px' },
    },
    // Специальные стили для блоков на одной строке
    rowContainer: {
        display: 'flex',
        gap: { xs: '6px', md: '8px' },
        marginBottom: '0px',
    },
    flexBlock: {
        flex: 1,
        border: '1px solid var(--divider-color)',
        borderRadius: { xs: '6px', md: '8px' },
        padding: { xs: '8px', md: '12px' },
        backgroundColor: 'var(--background-default)',
        transition: 'all 200ms ease-in-out',
        '&:hover': {
            borderColor: 'var(--primary-color)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }
    },
});

// Компонент для блока с заголовком секции
const BlockContainer = ({ icon = null, title = null, children, sx = {} }) => {
    const styles = getStyles();
    const hasHeader = icon || title;
    const isTitleString = typeof title === 'string';

    return (
        <Box sx={{ ...styles.blockContainer, ...sx }}>
            {hasHeader && (
                <Box sx={styles.blockHeader}>
                    {icon && (
                        <Box sx={styles.blockHeaderIcon}>
                            {icon}
                        </Box>
                    )}
                    {title && (
                        isTitleString ? (
                            <Typography sx={styles.blockHeaderTitle}>
                                {title}
                            </Typography>
                        ) : (
                            <Box sx={{ 
                                flex: 1, 
                                fontSize: styles.blockHeaderTitle.fontSize,
                                fontWeight: styles.blockHeaderTitle.fontWeight,
                                color: styles.blockHeaderTitle.color,
                                textTransform: styles.blockHeaderTitle.textTransform,
                                letterSpacing: styles.blockHeaderTitle.letterSpacing
                            }}>
                                {title}
                            </Box>
                        )
                    )}
                </Box>
            )}
            <Box sx={styles.blockContent}>
                {children}
            </Box>
        </Box>
    );
};

// Компонент для блока Header
const HeaderBlock = ({ host }) => {
    return (
        <BlockContainer
        // icon={<InfoIcon sx={{ fontSize: '14px', color: 'var(--primary-color)' }} />}
        >
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 600,
                    textAlign: 'center',
                    color: 'var(--text-primary)',
                    fontSize: { xs: '12px', md: '14px' },
                    lineHeight: '1.2',
                    margin: 0
                }}
            >
                {host.name}
            </Typography>
        </BlockContainer>
    );
};

// Компонент для блока Info
const InfoBlock = ({ host, hostType, violations, fixations, dispatch }) => {
    return (
        <BlockContainer
            icon={<SatelliteAltIcon sx={{ fontSize: { xs: '11px', md: '12px' }, color: 'var(--info-color)' }} />}
            title="Данные"
        >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '3px', md: '4px' } }}>
                {/* Время */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: { xs: '8px', md: '12px' } }}>
                    <TypographyStyled
                        variant="body3"
                        sx={{ 
                            fontSize: { xs: '10px', md: '11px' },
                            color: 'var(--text-secondary)',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                        }}
                    >
                        Время:
                    </TypographyStyled>
                    <TypographyStyled
                        variant="body3"
                        color="warning"
                        sx={{ fontSize: { xs: '10px', md: '11px' }, whiteSpace: 'nowrap', textAlign: 'right' }}
                    >
                        {hostType !== 'vehicle' ? (new Date()).toLocaleString('ru') : new Date(parseInt(host.items.find(item => item.key_.includes('_time'))?.lastvalue + '000' || 0)).toLocaleString('ru')}
                    </TypographyStyled>
                </Box>

                {/* Координаты */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: { xs: '8px', md: '12px' } }}>
                    <TypographyStyled
                        variant="body3"
                        sx={{ 
                            fontSize: { xs: '10px', md: '11px' },
                            color: 'var(--text-secondary)',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                        }}
                    >
                        Координаты:
                    </TypographyStyled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '3px', md: '4px' } }}>
                        <TypographyStyled
                            variant="body3"
                            color="primary"
                            sx={{ 
                                cursor: 'pointer', 
                                fontSize: { xs: '10px', md: '11px' }, 
                                whiteSpace: 'nowrap', 
                                textAlign: 'right',
                                transition: 'all 150ms ease',
                                '&:hover': {
                                    textDecoration: 'underline',
                                    opacity: 0.8
                                }
                            }}
                            onClick={() => window.open(`https://yandex.ru/maps/?whatshere[point]=${host.inventory.location_lon},${host.inventory.location_lat}&whatshere[zoom]=17`)}
                        >
                            {host.inventory.location_lat}, {host.inventory.location_lon}
                        </TypographyStyled>
                        <Tooltip title="Копировать координаты" arrow placement="top">
                            <ContentCopyIcon
                                sx={{ 
                                    fontSize: { xs: '11px', md: '12px' }, 
                                    color: 'var(--info-color)', 
                                    cursor: 'pointer', 
                                    flexShrink: 0,
                                    padding: { xs: '1px', md: '2px' },
                                    borderRadius: { xs: '3px', md: '4px' },
                                    transition: 'all 150ms ease',
                                    '&:hover': {
                                        backgroundColor: 'var(--info-color)',
                                        color: 'white',
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    },
                                    '&:active': {
                                        transform: 'scale(0.95)'
                                    }
                                }}
                                onClick={() => {
                                    navigator.clipboard.writeText(`${host.inventory.location_lat}, ${host.inventory.location_lon}`);
                                    dispatch(addNotification({ message: 'Координаты скопированы в буфер обмена', options: { variant: 'success' } }));
                                }}
                            />
                        </Tooltip>
                    </Box>
                </Box>

                {/* Нарушения и проезды */}
                {hostType !== 'vehicle' ? (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: { xs: '8px', md: '12px' } }}>
                            <TypographyStyled
                                variant="body3"
                                sx={{ 
                                    fontSize: { xs: '10px', md: '11px' },
                                    color: 'var(--text-secondary)',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }}
                            >
                                Нарушения:
                            </TypographyStyled>
                            <TypographyStyled variant="body3" color="error" sx={{ fontSize: { xs: '10px', md: '11px' }, fontWeight: 'bold', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                {violations}
                            </TypographyStyled>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: { xs: '8px', md: '12px' } }}>
                            <TypographyStyled
                                variant="body3"
                                sx={{ 
                                    fontSize: { xs: '10px', md: '11px' },
                                    color: 'var(--text-secondary)',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }}
                            >
                                Проезды:
                            </TypographyStyled>
                            <TypographyStyled variant="body3" color="success" sx={{ fontSize: { xs: '10px', md: '11px' }, fontWeight: 'bold', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                {fixations}
                            </TypographyStyled>
                        </Box>
                    </>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: { xs: '8px', md: '12px' } }}>
                            <TypographyStyled
                                variant="body3"
                                sx={{ 
                                    fontSize: { xs: '10px', md: '11px' },
                                    color: 'var(--text-secondary)',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }}
                            >
                                Скорость:
                            </TypographyStyled>
                            <TypographyStyled variant="body3" color="success" sx={{ fontSize: { xs: '10px', md: '11px' }, fontWeight: 'bold', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                {host.items.find(item => item.key_.includes('_speed'))?.lastvalue}
                            </TypographyStyled>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: { xs: '8px', md: '12px' } }}>
                            <TypographyStyled
                                variant="body3"
                                sx={{ 
                                    fontSize: { xs: '10px', md: '11px' },
                                    color: 'var(--text-secondary)',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }}
                            >
                                Курс:
                            </TypographyStyled>
                            <TypographyStyled variant="body3" color="secondary" sx={{ fontSize: { xs: '10px', md: '11px' }, fontWeight: 'bold', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                {host.items.find(item => item.key_.includes('_course'))?.lastvalue}
                            </TypographyStyled>
                        </Box>
                    </>
                )}
            </Box>
        </BlockContainer>
    );
};

// Компонент для блока Pautina
const PautinaBlock = ({ pautina }) => {
    // Функция для парсинга даты в формате "DD.MM.YYYY"
    const parseDate = (dateString) => {
        if (!dateString) return null;
        // Проверяем формат DD.MM.YYYY
        if (typeof dateString === 'string' && dateString.includes('.')) {
            const parts = dateString.split('.');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1; // месяцы в JS начинаются с 0
                const year = parseInt(parts[2], 10);
                return new Date(year, month, day);
            }
        }
        // Если формат другой, пытаемся стандартный парсинг
        return new Date(dateString);
    };

    // Функция для вычисления дней между датами
    const getDaysDifference = (dateString) => {
        if (!dateString) return 0;
        const targetDate = parseDate(dateString);
        if (!targetDate || isNaN(targetDate.getTime())) return 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        targetDate.setHours(0, 0, 0, 0);
        const diffTime = targetDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // Функция для форматирования дней
    const formatDays = (days) => {
        const absDays = Math.abs(days);
        if (absDays === 0) return 'сегодня';
        if (absDays === 1) return '1 день';
        if (absDays >= 2 && absDays <= 4) return `${absDays} дня`;
        return `${absDays} дней`;
    };

    if (!pautina || !pautina.warnings) {
        return (
            <BlockContainer
                icon={<WebIcon sx={{ fontSize: { xs: '11px', md: '12px' }, color: 'var(--secondary-color)' }} />}
                title="Паутина"
            >
                <TypographyStyled
                    variant="body3"
                    sx={{
                        color: 'var(--text-secondary)',
                        fontStyle: 'italic',
                        fontSize: { xs: '10px', md: '11px' },
                        textAlign: 'center'
                    }}
                >
                    Нет данных
                </TypographyStyled>
            </BlockContainer>
        );
    }

    const { warnings, data, time } = pautina;

    // Форматирование времени обновления
    const formatUpdateTime = (timeString) => {
        if (!timeString) return null;
        const date = new Date(timeString);
        if (isNaN(date.getTime())) return null;
        return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const updateTimeFormatted = formatUpdateTime(time);

    // Заголовок с временем обновления
    const titleWithTime = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '4px', md: '6px' }, color: 'var(--text-primary)' }}>
            <span>Паутина</span>
            {updateTimeFormatted && (
                <TypographyStyled
                    variant="caption"
                    sx={{
                        fontSize: { xs: '8px', md: '9px' },
                        color: 'var(--text-secondary)',
                        fontWeight: 'normal',
                        opacity: 0.8
                    }}
                >
                    ({updateTimeFormatted})
                </TypographyStyled>
            )}
        </Box>
    );

    // Вспомогательный компонент для строки с названием слева и данными справа
    const PautinaRow = ({ label, value, color }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: { xs: '8px', md: '12px' } }}>
            <TypographyStyled
                variant="body3"
                sx={{
                    color: 'var(--text-secondary)',
                    fontSize: { xs: '10px', md: '11px' },
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                }}
            >
                {label}
            </TypographyStyled>
            <TypographyStyled
                variant="body3"
                sx={{
                    color: color,
                    fontSize: { xs: '10px', md: '11px' },
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    textAlign: 'right'
                }}
            >
                {value}
            </TypographyStyled>
        </Box>
    );

    // Обработка поверки
    const renderPoverka = () => {
        if (warnings.poverka_expiring) {
            const daysLeft = getDaysDifference(data?.poverka_to);
            return <PautinaRow label="Поверка" value={`истекает (через ${formatDays(daysLeft)})`} color="var(--warning-color)" />;
        } else if (warnings.poverka) {
            return <PautinaRow label="Поверка" value="актуальна" color="var(--success-color)" />;
        } else {
            const poverkaDate = parseDate(data?.poverka_to);
            if (!poverkaDate || isNaN(poverkaDate.getTime())) {
                return <PautinaRow label="Поверка" value="Истекла" color="var(--error-color)" />;
            }
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            poverkaDate.setHours(0, 0, 0, 0);
            const diffTime = today - poverkaDate;
            const daysAgo = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            return <PautinaRow label="Поверка" value={`Истекла (${formatDays(daysAgo)} назад)`} color="var(--error-color)" />;
        }
    };

    // Обработка статуса
    const renderStatus = () => (
        <PautinaRow
            label="Статус"
            value={warnings.status ? 'утвержден' : 'не утвержден'}
            color={warnings.status ? 'var(--success-color)' : 'var(--error-color)'}
        />
    );

    // Обработка работоспособности
    const renderPerformance = () => (
        <PautinaRow
            label="Работоспособность"
            value={warnings.performance ? 'работает' : 'не работает'}
            color={warnings.performance ? 'var(--success-color)' : 'var(--error-color)'}
        />
    );

    // Обработка утвержденных нарушений
    const renderApprovedViolations = () => (
        <PautinaRow
            label="Утвержденные нарушения"
            value={warnings.approved_vilations ? 'есть' : 'нет'}
            color={warnings.approved_vilations ? 'var(--success-color)' : 'var(--error-color)'}
        />
    );

    // Обработка расписания
    const renderSchedule = () => (
        <PautinaRow
            label="Расписание"
            value={warnings.schedule ? 'есть' : 'нет'}
            color={warnings.schedule ? 'var(--error-color)' : 'var(--success-color)'}
        />
    );

    return (
        <BlockContainer
            icon={<WebIcon sx={{ fontSize: { xs: '11px', md: '12px' }, color: 'var(--secondary-color)' }} />}
            title={titleWithTime}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '3px', md: '4px' } }}>
                {renderPoverka()}
                {renderStatus()}
                {renderPerformance()}
                {renderApprovedViolations()}
                {renderSchedule()}
            </Box>
        </BlockContainer>
    );
};

// Компонент для блока Notes
const NotesBlock = ({ notes, guestNotes, bitrixNotes, host, hostType, roles, onEditClick, showGuestNotes, onToggleGuestNotes }) => {
    const renderNotes = (notesList, source, isGuest = false) => {
        return (
            <Box sx={{ display: "flex", flexDirection: 'column', gap: { xs: '3px', md: '4px' }, alignItems: 'center' }}>
                {notesList.map((note, index) => (
                    <Box key={`${source}-${index}`} sx={{ display: "flex", flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: { xs: '6px', md: '8px' } }}>
                        <TypographyStyled
                            variant="body3"
                            sx={{
                                color: isGuest ? 'var(--success-color)' : (source === 'Zabbix' ? 'red' : 'blue'),
                                fontStyle: 'italic',
                                fontSize: { xs: '10px', md: '11px' }
                            }}
                        >
                            <b>{source}</b>
                        </TypographyStyled>
                        {hostType === 'crossroad' && (
                            <TypographyStyled
                                variant="body3"
                                color='var(--text-primary)'
                                sx={{ textDecoration: 'underline', whiteSpace: 'nowrap', fontSize: { xs: '10px', md: '11px' } }}
                            >
                                <b>{note[0]}</b>
                            </TypographyStyled>
                        )}
                        <TypographyStyled
                            variant="body3"
                            color='var(--note-color)'
                            sx={{ fontSize: { xs: '10px', md: '11px' } }}
                        >
                            {note[1]}
                        </TypographyStyled>
                    </Box>
                ))}
            </Box>
        );
    };

    // Выбираем заметки в зависимости от режима
    const displayNotes = showGuestNotes ? guestNotes : notes;
    const hasDisplayNotes = displayNotes.length > 0 || bitrixNotes.length > 0;
    const hasAnyNotes = notes.length > 0 || guestNotes.length > 0 || bitrixNotes.length > 0;
    const canEdit = roles && !roles.includes('operator');

    // Проверяем есть ли оба типа заметок для показа переключателя
    const hasEngineerNotes = notes.length > 0;
    const hasGuestNotesData = guestNotes.length > 0;
    const showToggle = hasEngineerNotes || hasGuestNotesData;

    return (
        <BlockContainer
            icon={<NotesIcon sx={{ fontSize: { xs: '11px', md: '12px' }, color: 'var(--info-color)' }} />}
            title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, gap: { xs: '3px', md: '4px' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '3px', md: '4px' }, color: 'var(--text-primary)' }}>
                        <span>Заметки</span>
                        {showGuestNotes ? (
                            <Typography sx={{ fontSize: { xs: '8px', md: '9px' }, color: 'var(--text-secondary)', fontWeight: 500 }}>
                                (гости)
                            </Typography>
                        ) : (
                            <Typography sx={{ fontSize: { xs: '8px', md: '9px' }, color: 'var(--text-secondary)', fontWeight: 500 }}>
                                (инженер)
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '1px', md: '2px' } }}>
                        {canEdit && onEditClick && (
                            <Tooltip title="Редактировать заметки" arrow placement="top">
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        onEditClick();
                                    }}
                                    size="small"
                                    sx={{
                                        padding: { xs: '3px', md: '4px' },
                                        width: { xs: '18px', md: '20px' },
                                        height: { xs: '18px', md: '20px' },
                                        color: 'var(--info-color)',
                                        '&:hover': {
                                            backgroundColor: 'var(--info-color)',
                                            color: 'white',
                                            transform: 'scale(1.1)',
                                        }
                                    }}
                                >
                                    <EditIcon sx={{ fontSize: { xs: '12px', md: '14px' } }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {showToggle && (
                            <Tooltip title={showGuestNotes ? "Показать заметки инженера" : "Показать заметки для гостей"} arrow placement="top">
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        onToggleGuestNotes();
                                    }}
                                    size="small"
                                    sx={{
                                        padding: { xs: '3px', md: '4px' },
                                        width: { xs: '18px', md: '20px' },
                                        height: { xs: '18px', md: '20px' },
                                        color: showGuestNotes ? 'var(--success-color)' : 'var(--warning-color)',
                                        '&:hover': {
                                            backgroundColor: showGuestNotes ? 'var(--success-color)' : 'var(--warning-color)',
                                            color: 'white',
                                            transform: 'scale(1.1)',
                                        }
                                    }}
                                >
                                    {showGuestNotes ? (
                                        <VisibilityIcon sx={{ fontSize: { xs: '12px', md: '14px' } }} />
                                    ) : (
                                        <EngineeringIcon sx={{ fontSize: { xs: '12px', md: '14px' } }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            }
        >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {hasDisplayNotes ? (
                    <>
                        {renderNotes(displayNotes, showGuestNotes ? 'Гость' : 'Zabbix', showGuestNotes)}
                        {!showGuestNotes && renderNotes(bitrixNotes, 'Bitrix')}
                    </>
                ) : (
                    <TypographyStyled
                        variant="body3"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontStyle: 'italic',
                            fontSize: { xs: '10px', md: '11px' },
                            textAlign: 'center'
                        }}
                    >
                        Нет
                    </TypographyStyled>
                )}
            </Box>
        </BlockContainer>
    );
};

// Словарь переводов для типов проблем
const problemTranslations = {
    'Zabbix agent is not available': ' Узел :: Zabbix агент недоступен'
    // Добавьте другие переводы по необходимости
};

// Функция для перевода
const translateProblemType = (text) => {
    return problemTranslations[text] || text;
};

// Компонент для блока Problems
const ProblemsBlock = ({ problems, hostType }) => {
    const renderProblems = () => {
        if (hostType === 'deactivated') {
            return (
                <Box sx={{ display: "flex", flexDirection: 'row', justifyContent: 'center', gap: { xs: '4px', md: '5px' } }}>
                    <CancelRoundedIcon sx={{ fontSize: { xs: '11px', md: '12px' }, color: 'var(--error-color)' }} />
                    <TypographyStyled
                        variant="body3"
                        color="error"
                        sx={{ fontSize: { xs: '10px', md: '11px' }, fontWeight: 'bold' }}
                    >
                        Деактивирован
                    </TypographyStyled>
                </Box>
            );
        }
        if (problems.length === 0) {
            return (
                <Box sx={{ display: "flex", flexDirection: 'row', justifyContent: 'center', gap: { xs: '4px', md: '5px' } }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: { xs: '11px', md: '12px' }, color: 'var(--success-color)' }} />
                    <TypographyStyled
                        variant="body3"
                        color="success"
                        sx={{ fontSize: { xs: '10px', md: '11px' }, fontWeight: 'bold' }}
                    >
                        Работа в штатном режиме
                    </TypographyStyled>
                </Box>
            );
        }
        // Сортируем проблемы по названию (element[2])
        const sortedProblems = [...problems].sort((a, b) => {
            return a[2].localeCompare(b[2]);
        });
        return (
            <Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: { xs: '3px', md: '4px' } }}>
                {sortedProblems.map(element => (
                    <Box key={element[0]} sx={{ display: "flex", flexDirection: 'row', alignItems: 'center', gap: { xs: '4px', md: '6px' } }}>
                        {hostType === 'crossroad' && (
                            <TypographyStyled
                                variant="body3"
                                color='var(--text-primary)'
                                sx={{ textDecoration: 'underline', whiteSpace: 'nowrap', fontSize: { xs: '10px', md: '11px' } }}
                            >
                                <b>{element[1].split('/')[1]}</b>
                            </TypographyStyled>
                        )}
                        <TypographyStyled
                            variant="body3"
                            color={element[4]}
                            sx={{ fontSize: { xs: '10px', md: '11px' }, fontWeight: 'bold' }}
                        >
                            {translateProblemType(element[2])}: {element[3]}
                        </TypographyStyled>
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <BlockContainer
            icon={<BugReportIcon sx={{ fontSize: { xs: '11px', md: '12px' }, color: problems.length === 0 ? 'var(--success-color)' : 'var(--warning-color)' }} />}
            title="Состояние"
        >
            {renderProblems()}
        </BlockContainer>
    );
};

// Стили для кнопок управления
const ActionButton = ({ icon, label, onClick, color = 'var(--primary-color)', tooltip }) => (
    <Tooltip title={tooltip || label} arrow placement="top">
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '3px', md: '4px' },
                padding: { xs: '3px 6px', md: '4px 8px' },
                borderRadius: { xs: '4px', md: '6px' },
                backgroundColor: 'var(--background-paper)',
                border: '1px solid var(--divider-color)',
                cursor: 'pointer',
                transition: 'all 150ms ease',
                '&:hover': {
                    backgroundColor: color,
                    borderColor: color,
                    '& .action-icon, & .action-label': {
                        color: 'white',
                    },
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                },
                '&:active': {
                    transform: 'translateY(0)',
                }
            }}
        >
            <Box className="action-icon" sx={{ display: 'flex', color: color, transition: 'color 150ms' }}>
                {icon}
            </Box>
            {label && (
                <Typography
                    className="action-label"
                    sx={{
                        fontSize: { xs: '9px', md: '10px' },
                        fontWeight: 600,
                        color: color,
                        whiteSpace: 'nowrap',
                        transition: 'color 150ms'
                    }}
                >
                    {label}
                </Typography>
            )}
        </Box>
    </Tooltip>
);

// Компонент для блока Footer
const FooterBlock = ({ host, hostType, roles, infoClick, renderVgkButtons }) => {
    if (hostType === 'vehicle') return null;

    const canManage = roles && !roles.includes('operator');

    return (
        <BlockContainer
            icon={<MoreHorizIcon sx={{ fontSize: { xs: '11px', md: '12px' }, color: 'var(--secondary-color)' }} />}
            title="Действия"
        >
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>


                {/* Кнопки для управления - только для не-операторов */}
                {canManage && (
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: { xs: '4px', md: '6px' }
                    }}>
                        <PopupLinksDropdown host={host} hostType={hostType} />
                        <PopupScriptsDropdown host={host} hostType={hostType} />
                        {hostType === 'vgk' && renderVgkButtons()}
                    </Box>
                )}

                {/* Кнопка подробнее - всегда показываем */}
                <ActionButton
                    icon={<OpenInNewIcon sx={{ fontSize: { xs: '12px', md: '14px' } }} />}
                    label="Подробнее"
                    onClick={infoClick}
                    color="var(--info-color)"
                    tooltip="Открыть подробную информацию"
                />
            </Box>
        </BlockContainer>
    );
};

const MarkerPopup = ({ hostid, hostType = 'default', onRequestClose }) => {
    const map = useMap();
    const user = useSelector(state => state.store.user);
    const objects = useSelector(state => state.store.objects);
    const dispatch = useDispatch();
    const { roles } = user || {};
    const styles = getStyles();
    const [hostsDataDialogOpen, setHostsDataDialogOpen] = useState(false);
    const [selectedHostForEdit, setSelectedHostForEdit] = useState(null);
    const [showGuestNotes, setShowGuestNotes] = useState(false);
    const lastHostRef = useRef(null);
    const lastPositionRef = useRef(null);

    // Получаем host из store по hostid и hostType
    const hostFromStore = useMemo(() => {
        if (!hostid || !objects) return null;

        // Определяем источник данных в зависимости от типа
        const dataSource = hostType === 'vehicle'
            ? objects.vehicles
            : hostType.startsWith('@')
                ? objects.others?.[hostType]
                : objects[hostType];

        if (!dataSource || !Array.isArray(dataSource)) return null;

        // Ищем host по hostid
        let foundHost = dataSource.find(h => {
            if (h.hostid === hostid || h.complex === hostid) {
                return true;
            }
            // Для crossroad также проверяем child элементы
            if (hostType === 'crossroad' && h.child) {
                return h.child.some(child => child.hostid === hostid || child.complex === hostid);
            }
            return false;
        });

        // Если нашли в child, возвращаем child элемент
        if (foundHost && hostType === 'crossroad' && foundHost.child) {
            const childHost = foundHost.child.find(child => child.hostid === hostid || child.complex === hostid);
            if (childHost) {
                return childHost;
            }
        }

        return foundHost || null;
    }, [hostid, hostType, objects]);

    // Важно: не даём Popup "схлопнуться", если на короткий момент host не найден во время обновления стейта
    const host = hostFromStore || lastHostRef.current;
    if (hostFromStore) lastHostRef.current = hostFromStore;
    const positionFromStore = useMemo(() => {
        if (!hostFromStore) return null;

        if (hostType === 'vehicle') {
            let lat = hostFromStore.items?.find(item => item.key_.includes('_lat'))?.lastvalue;
            let lon = hostFromStore.items?.find(item => item.key_.includes('_lon'))?.lastvalue;
            if (!lat && hostFromStore.inventory?.location_lat) {
                lat = hostFromStore.inventory.location_lat;
            }
            if (!lon && hostFromStore.inventory?.location_lon) {
                lon = hostFromStore.inventory.location_lon;
            }
            return lat && lon ? [lat, lon] : null;
        }
        return hostFromStore.inventory?.location_lat && hostFromStore.inventory?.location_lon
            ? [hostFromStore.inventory.location_lat, hostFromStore.inventory.location_lon]
            : null;
    }, [hostFromStore, hostType]);

    const position = positionFromStore || lastPositionRef.current;
    if (positionFromStore) lastPositionRef.current = positionFromStore;



    // Получаем данные в зависимости от типа
    const getComplexData = useCallback(() => {
        if (!host) return {}
        let problems = getProblems(host);
        let notes = getNote(host);
        let guestNotes = getGuestNote(host);
        let bitrixNotes = getBitrixNote(host);
        const { violations, fixations } = violationsFixations(host);

        if (hostType === 'crossroad') {
            // Для crossroad собираем данные из дочерних элементов
            if (host.child) {
                host.child.forEach(host => {
                    problems = [...getProblems(host), ...problems];
                    notes = [...notes, ...getNote(host)];
                    guestNotes = [...guestNotes, ...getGuestNote(host)];
                    bitrixNotes = [...bitrixNotes, ...getBitrixNote(host)];
                });
            }
        }

        return { problems, notes, guestNotes, bitrixNotes, violations, fixations };
    }, [host]);

    const { problems, notes, guestNotes, bitrixNotes, violations, fixations } = getComplexData();

    const infoClick = () => {
        map.fire('openModal', {
            variant: 'info',
            host: host,
            hostType: hostType
        });
    };

    const handleEditNotes = () => {
        // Определяем, какой хост редактировать
        let targetHost = null;
        if (hostType === 'crossroad' && notes.length > 0) {
            // Берем первый хост из заметок
            targetHost = notes[0][0];
        }
        setSelectedHostForEdit(targetHost);
        setHostsDataDialogOpen(true);
    };

    // Определяем максимальную ширину в зависимости от типа
    const getMaxWidth = () => {
        // Если есть данные Паутины, нужно больше места
        const hasPautina = host?.pautina?.data;
        const isMobile = window.innerWidth < 900;
        
        if (isMobile) {
            // Для мобильных устройств используем меньшую ширину
            return 280;
        }
        
        switch (hostType) {
            case 'crossroad':
                return 550;
            case 'speed':
            case 'vgk':
                return hasPautina ? 550 : 400;
            default:
                // Для @-типов (others) тоже используем увеличенную ширину если есть паутина
                return hasPautina ? 550 : 400;
        }
    };

    // Определяем максимальную ширину заголовка
    const getHeaderMaxWidth = () => {
        const isMobile = window.innerWidth < 900;
        
        if (isMobile) {
            return '250px';
        }
        
        switch (hostType) {
            case 'crossroad':
                return '400px';
            case 'speed':
            case 'vgk':
            default:
                return '350px';
        }
    };


    // Рендерим дополнительные кнопки для VGK
    const renderVgkButtons = () => {
        if (hostType !== 'vgk' || !host.bitrix?.regular_task) return null;

        return (
            <Tooltip title="Регламентная задача в Битрикс" arrow placement="top">
                <IconButton
                    onClick={infoClick}
                    size="small"
                    sx={{
                        padding: { xs: '3px', md: '4px' },
                        width: { xs: '20px', md: '24px' },
                        height: { xs: '20px', md: '24px' },
                        borderRadius: { xs: '4px', md: '6px' },
                        backgroundColor: 'var(--error-color)',
                        color: 'white',
                        transition: 'all 0.2s ease-in-out',
                        marginRight: { xs: '3px', md: '4px' },
                        '&:hover': {
                            backgroundColor: 'var(--error-color)',
                            transform: 'scale(1.05)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                        },
                        '&:active': {
                            transform: 'scale(0.95)',
                        }
                    }}
                >
                    <Box
                        component="svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        sx={{
                            width: { xs: '12px', md: '16px' },
                            height: { xs: '12px', md: '16px' }
                        }}
                    >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                            fill="currentColor" />
                    </Box>
                </IconButton>
            </Tooltip>
        );
    };

    // Если host ещё ни разу не был найден — не рендерим popup
    if (!host || !position) return null;

    return (
        <>
            <Popup
                position={position}
                // не даём Leaflet автоматически закрывать popup (иначе он "схлопывается" при пересоздании слоёв/кластеров)
                autoClose={false}
                closeOnClick={false}
                closeOnEscapeKey={false}
                closeButton={false}
                // maxWidth={getMaxWidth()}
                offset={hostType === 'vehicle' ? [0, -10] : [0, 0]}
            >
                <Box sx={{ display: "flex", flexDirection: 'column', gap: { xs: '3px', md: '4px' } }}>
                    {/* Кнопка закрытия (управляем закрытием сами, не через leaflet popupclose) */}
                    <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 9999 }}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                try {
                                    map.closePopup();
                                } finally {
                                    onRequestClose?.();
                                }
                            }}
                            sx={{
                                width: { xs: 20, md: 24 },
                                height: { xs: 20, md: 24 },
                                borderRadius: '50%',
                                color: 'var(--text-secondary)',
                                backgroundColor: 'var(--background-paper)',
                                border: '1px solid var(--divider-color)',
                                '&:hover': {
                                    color: 'white',
                                    backgroundColor: 'var(--error-color)',
                                }
                            }}
                        >
                            <CloseIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                        </IconButton>
                    </Box>

                    {/* 1. Header - наименование */}
                    <HeaderBlock host={host} />

                    {/* Определяем какие блоки показывать в зависимости от hostType */}
                    {(() => {
                        const isOtherType = hostType?.startsWith('@');
                        const isVehicle = hostType === 'vehicle';

                        // Для @ типов: только данные и паутина
                        // Для vehicle: только данные и проблемы  
                        // Для остальных: все блоки

                        const showPautina = isOtherType || (!isVehicle);
                        const showNotes = !isOtherType && !isVehicle;
                        const showProblems = !isOtherType;
                        const showFooter = !isVehicle;

                        return (
                            <>
                                {/* 2 и 3 блоки на одной строке */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: { xs: '3px', md: '4px' }, 
                                    width: '100%',
                                    flexWrap: 'wrap'
                                }}>
                                    {/* 2. Info - информация */}
                                    <Box sx={{ flex: '1 1 auto', minWidth: 'fit-content' }}>
                                        <InfoBlock
                                            host={host}
                                            hostType={hostType}
                                            violations={violations}
                                            fixations={fixations}
                                            dispatch={dispatch}
                                        />
                                    </Box>

                                    {/* 3. Pautina - данные из паутины */}
                                    {showPautina && (
                                        <Box sx={{ flex: '1 1 auto', minWidth: 'fit-content' }}>
                                            <PautinaBlock pautina={host?.pautina} />
                                        </Box>
                                    )}
                                </Box>

                                {/* 4. Notes - заметки */}
                                {showNotes && (
                                    <NotesBlock
                                        notes={notes}
                                        guestNotes={guestNotes}
                                        bitrixNotes={bitrixNotes}
                                        host={host}
                                        hostType={hostType}
                                        roles={roles}
                                        onEditClick={handleEditNotes}
                                        showGuestNotes={showGuestNotes}
                                        onToggleGuestNotes={() => setShowGuestNotes(!showGuestNotes)}
                                    />
                                )}

                                {/* 5. Problems - проблемы */}
                                {showProblems && (
                                    <ProblemsBlock problems={problems} hostType={hostType} />
                                )}

                                {/* 6. Footer - кнопки */}
                                {showFooter && (
                                    <FooterBlock
                                        host={host}
                                        hostType={hostType}
                                        roles={roles}
                                        infoClick={infoClick}
                                        renderVgkButtons={renderVgkButtons}
                                    />
                                )}
                            </>
                        );
                    })()}
                </Box>
            </Popup>

            {/* Диалог для редактирования заметок */}
            <HostsDataDialog
                open={hostsDataDialogOpen}
                onClose={() => {
                    setHostsDataDialogOpen(false);
                    setSelectedHostForEdit(null);
                }}
                host={host}
                hostType={hostType}
                targetHost={selectedHostForEdit}
            />
        </>
    );
};

export default MarkerPopup;